import React, { createContext, useContext, useState, useEffect } from 'react';

interface BannerContextType {
  isBannerVisible: boolean;
  setBannerVisible: (visible: boolean) => void;
  bannerHeight: number;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export const useBanner = () => {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error('useBanner must be used within a BannerProvider');
  }
  return context;
};

export const BannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(44);

  // Update banner height based on screen size
  useEffect(() => {
    const updateBannerHeight = () => {
      const isMobile = window.innerWidth < 640; // sm breakpoint
      setBannerHeight(isMobile ? 41 : 40); // Match actual banner height: py-1 (8px) + content height (~32px)
    };

    updateBannerHeight();
    window.addEventListener('resize', updateBannerHeight);
    return () => window.removeEventListener('resize', updateBannerHeight);
  }, []);

  useEffect(() => {
    // Check if banner was previously dismissed
    const bannerDismissed = localStorage.getItem('topBannerDismissed');
    setIsBannerVisible(!bannerDismissed);
  }, []);

  const setBannerVisible = (visible: boolean) => {
    setIsBannerVisible(visible);
    if (!visible) {
      localStorage.setItem('topBannerDismissed', 'true');
    }
  };

  return (
    <BannerContext.Provider value={{ isBannerVisible, setBannerVisible, bannerHeight }}>
      {children}
    </BannerContext.Provider>
  );
};